import { useState, useEffect } from 'react';
import { X, CaretRight, CaretLeft, Moon } from '@phosphor-icons/react';
import { supabase } from '@/integrations/supabase/client';

const monoFont = "'IBM Plex Mono', monospace";

interface DarkModeSettings {
  dark_mode_enabled: boolean;
  dark_bg_color: string;
  dark_text_color: string;
}

interface TutorialStep {
  title: string;
  description: string;
  tab?: string;
}

const STEPS: TutorialStep[] = [
  {
    title: 'Bem-vindo ao CMS',
    description: 'Este painel permite gerenciar todo o conteúdo do seu site. Vamos fazer um tour rápido pelas funcionalidades.',
  },
  {
    title: 'Galeria Home',
    description: 'Na aba HOME, gerencie as imagens da página principal. Arraste fotos para a área de upload, reordene com drag & drop, junte ou separe pares de imagens.',
    tab: 'home',
  },
  {
    title: 'Gerenciar Menu',
    description: 'Na aba Menu, adicione, remova e reordene os itens de navegação. Vincule seções ou URLs externas. Arraste para reordenar.',
    tab: 'menu',
  },
  {
    title: 'Seções de Projetos',
    description: 'Nas abas de seções (ex: Exposições), crie projetos com diferentes layouts. Cada projeto pode ser publicado ou mantido como rascunho.',
    tab: 'exposicoes',
  },
  {
    title: 'Layouts de Galeria',
    description: 'A — Split Vertical: transição com divisão vertical. B — Tripa Vertical: scroll contínuo. C — Tripa Horizontal: scroll lateral. D — Split Horizontal: transição com divisão horizontal.',
  },
  {
    title: 'Aprovação de Clientes',
    description: 'Na aba Aprovação, crie páginas protegidas por senha para clientes aprovarem fotos. Compartilhe o link e a senha com seus clientes.',
    tab: 'aprovacao',
  },
  {
    title: 'Aparência',
    description: 'Na aba Aparência, personalize tipografia, cores (light/dark mode), transições de hover, posição do menu, cursor personalizado e efeitos de reveal.',
    tab: 'aparencia',
  },
  {
    title: 'Configurações',
    description: 'Na aba Configurações, defina o título do site, descrição SEO, favicon, imagem de compartilhamento social e integração com Google Analytics.',
    tab: 'configuracoes',
  },
  {
    title: 'Pronto!',
    description: 'Você está pronto para usar o CMS. Clique em "Visualizar site" no topo para ver as mudanças. Este tutorial pode ser reaberto a qualquer momento.',
  },
];

interface CmsTutorialProps {
  onNavigateTab?: (tab: string) => void;
  onClose: () => void;
}

export function CmsTutorial({ onNavigateTab, onClose }: CmsTutorialProps) {
  const [step, setStep] = useState(0);
  const [darkMode, setDarkMode] = useState<DarkModeSettings | null>(null);
  const current = STEPS[step];

  useEffect(() => {
    const loadDarkMode = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('dark_mode_enabled, dark_bg_color, dark_text_color')
        .limit(1)
        .maybeSingle();
      if (data) setDarkMode(data as unknown as DarkModeSettings);
    };
    loadDarkMode();

    // Listen for dark mode changes
    const handler = (e: CustomEvent) => {
      setDarkMode({
        dark_mode_enabled: e.detail.dark_mode_enabled,
        dark_bg_color: e.detail.dark_bg_color,
        dark_text_color: e.detail.dark_text_color,
      });
    };
    window.addEventListener('cms-dark-mode-change' as any, handler);
    return () => window.removeEventListener('cms-dark-mode-change' as any, handler);
  }, []);

  useEffect(() => {
    if (current.tab && onNavigateTab) {
      onNavigateTab(current.tab);
    }
  }, [step]);

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else onClose();
  };
  const prev = () => { if (step > 0) setStep(step - 1); };


  const dk = darkMode?.dark_mode_enabled;
  const bgColor = dk ? darkMode.dark_bg_color : 'hsl(var(--background))';
  const textColor = dk ? darkMode.dark_text_color : 'hsl(var(--foreground))';
  const borderColor = dk ? `${darkMode.dark_text_color}15` : 'hsl(0 0% 0% / 0.15)';
  const progressColor = dk ? darkMode.dark_text_color : 'hsl(0 0% 0%)';

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998] bg-foreground/30" onClick={onClose} />

      {dk && (
        <div className="fixed top-4 right-4 z-[10000] flex items-center gap-2 px-3 py-2" style={{
          backgroundColor: `${darkMode.dark_text_color}08`,
          border: `1px solid ${darkMode.dark_text_color}15`,
        }}>
          <Moon size={11} weight="thin" style={{ color: darkMode.dark_text_color, opacity: 0.6 }} />
          <span className="text-[10px] uppercase tracking-wider" style={{ fontFamily: monoFont, color: darkMode.dark_text_color, opacity: 0.5 }}>
            Dark mode ativo
          </span>
        </div>
      )}

      {/* Tutorial card */}
      <div
        className="fixed z-[9999] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md p-6"
        style={{
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          fontFamily: monoFont,
          color: textColor,
        }}
      >
        {/* Close */}
        <button onClick={onClose} className="absolute top-3 right-3 opacity-40 hover:opacity-100" style={{ color: textColor }}>
          <X size={16} weight="thin" />
        </button>

        {/* Progress */}
        <div className="flex gap-1 mb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="h-0.5 flex-1 transition-colors"
              style={{ backgroundColor: i <= step ? progressColor : `${progressColor}15` }}
            />
          ))}
        </div>

        {/* Step counter */}
        <span className="text-[10px] uppercase opacity-40" style={{ color: textColor }}>
          {step + 1} / {STEPS.length}
        </span>

        {/* Content */}
        <h3 className="text-sm uppercase font-light tracking-wider mt-2 mb-3" style={{ color: textColor }}>{current.title}</h3>
        <p className="text-xs leading-relaxed opacity-70" style={{ color: textColor }}>{current.description}</p>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={prev}
            disabled={step === 0}
            className="uppercase text-[10px] inline-flex items-center gap-1 disabled:opacity-20"
            style={{ fontFamily: monoFont, color: textColor }}
          >
            <CaretLeft size={12} weight="thin" />
            Anterior
          </button>
          <button
            onClick={next}
            className="uppercase text-[10px] px-4 py-2 inline-flex items-center gap-1"
            style={{ fontFamily: monoFont, border: `1px solid ${textColor}`, color: textColor }}
          >
            {step === STEPS.length - 1 ? 'Começar' : 'Próximo'}
            {step < STEPS.length - 1 && <CaretRight size={12} weight="thin" />}
          </button>
        </div>
      </div>
    </>
  );
}
